import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

// Define which routes each role can access
const ROLE_ROUTES = {
  super_admin:      ['/', '/candidates', '/employees', '/training', '/i9', '/alerts', '/audit', '/compliance-dashboard', '/logs'],
  compliance_admin: ['/', '/candidates', '/employees', '/training', '/i9', '/alerts', '/audit', '/compliance-dashboard', '/logs'],
  hr:               ['/', '/candidates', '/i9'],
  screening:        ['/', '/candidates'],
  recruitment:      ['/', '/candidates'],
  manager:          ['/', '/employees', '/alerts', '/audit', '/compliance-dashboard'],
  viewer:           ['/', '/compliance-dashboard', '/alerts'],
};

const ALL_NAV = [
  { to: '/',                    label: 'Dashboard',       icon: HomeIcon },
  { to: '/candidates',          label: 'New Bench',       icon: CandidatesIcon },
  { to: '/employees',           label: 'Employees',       icon: UsersIcon },
  { to: '/training',            label: 'Training',        icon: TrainingIcon },
  { to: '/i9',                  label: 'I-9 Workflow',    icon: I9Icon },
  { to: '/alerts',              label: 'Alerts',          icon: BellIcon },
  { to: '/audit',               label: 'Audit Readiness', icon: ShieldIcon },
  { to: '/compliance-dashboard',label: 'Reports',         icon: ReportsIcon },
  { to: '/logs',                label: 'Activity Logs',   icon: LogsIcon },
];

export default function Sidebar() {
  const { logout, admin } = useAuth();
  const navigate          = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const role    = admin?.role || 'viewer';
  const allowed = ROLE_ROUTES[role] || ['/'];
  const NAV     = ALL_NAV.filter((item) => allowed.includes(item.to));

  async function handleLogout() {
    await logout();
    navigate('/login');
  }

  return (
    <aside
      className={`
        flex flex-col bg-blue-800 text-white transition-all duration-300 shrink-0
        ${collapsed ? 'w-16' : 'w-60'}
      `}
    >
      {/* Logo / Brand */}
      <div className="flex items-center gap-3 px-4 py-5 border-b border-blue-700">
        <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center shrink-0">
          <ShieldIcon className="w-5 h-5 text-blue-800" />
        </div>
        {!collapsed && (
          <div>
            <p className="font-bold text-sm leading-tight">Compliance</p>
            <p className="text-blue-300 text-xs">Tracker</p>
          </div>
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="ml-auto text-blue-300 hover:text-white p-1"
          title={collapsed ? 'Expand' : 'Collapse'}
        >
          {collapsed ? <ChevronRightIcon className="w-4 h-4" /> : <ChevronLeftIcon className="w-4 h-4" />}
        </button>
      </div>

      {/* Navigation links */}
      <nav className="flex-1 py-4 space-y-1 overflow-y-auto">
        {NAV.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            end={to === '/'}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg mx-2 sidebar-link
               ${isActive ? 'bg-white/20 text-white' : 'text-blue-200 hover:bg-white/10 hover:text-white'}`
            }
            title={collapsed ? label : undefined}
          >
            <Icon className="w-5 h-5 shrink-0" />
            {!collapsed && <span>{label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* Quick action buttons — shown only to roles that have access */}
      {!collapsed && (allowed.includes('/employees') || allowed.includes('/candidates')) && (
        <div className="px-4 py-3 border-t border-blue-700 space-y-2">
          {allowed.includes('/employees') && (
            <button
              onClick={() => navigate('/employees/create')}
              className="w-full bg-white text-blue-800 text-sm font-semibold py-2 px-3 rounded-lg hover:bg-blue-50 transition-colors"
            >
              + Add Employee
            </button>
          )}
          {allowed.includes('/candidates') && (
            <button
              onClick={() => navigate('/candidates/create')}
              className="w-full bg-blue-600 text-white text-sm font-semibold py-2 px-3 rounded-lg hover:bg-blue-500 transition-colors border border-blue-500"
            >
              + Add New Bench
            </button>
          )}
        </div>
      )}

      {/* Admin info + logout */}
      <div className={`p-4 border-t border-blue-700 flex items-center gap-3 ${collapsed ? 'justify-center' : ''}`}>
        <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center shrink-0 text-xs font-bold uppercase">
          {admin?.username?.[0] ?? 'A'}
        </div>
        {!collapsed && (
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{admin?.username}</p>
            <p className="text-xs text-blue-300 truncate capitalize">{admin?.role?.replace('_',' ') ?? admin?.email}</p>
          </div>
        )}
        <button
          onClick={handleLogout}
          className="text-blue-300 hover:text-white p-1 shrink-0"
          title="Logout"
        >
          <LogoutIcon className="w-4 h-4" />
        </button>
      </div>
    </aside>
  );
}

// ── Inline SVG icons ──────────────────────────────────────────────────────────
function HomeIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
  );
}
function UsersIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}
function CandidatesIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );
}
function BellIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    </svg>
  );
}
function ShieldIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  );
}
function I9Icon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
    </svg>
  );
}
function ReportsIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  );
}
function LogoutIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
    </svg>
  );
}
function ChevronLeftIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
    </svg>
  );
}
function ChevronRightIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );
}
function LogsIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
    </svg>
  );
}
function TrainingIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l9-5-9-5-9 5 9 5z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0112 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
    </svg>
  );
}
